
// src/types.ts

// Represents the structure of a company in Firestore
export interface Company {
    id?: string;
    name: string;
    type: CompanyType;
    websiteUrl: string;
    legalEntityName: string;
    legalEntityWebsiteUrl: string;
    hqCountry: string;
    hqCity: string;
    hqState: string;
    hqZip: string;
    hqAddress: string;
    // Add a field to store references to generated reports
    generatedReportIds?: string[];
}

export enum CompanyType {
    VENDOR = 'Vendor',
    PARTNER = 'Partner',
    CUSTOMER = 'Customer',
    DISTRIBUTOR = 'Distributor'
}

// Represents a company's detailed profile
export interface CompanyProfile {
    id?: string;
    name: string;
    type: CompanyType;
    websiteUrl: string;
    legalEntityName: string;
    legalEntityWebsiteUrl: string;
    hqCountry: string;
    hqCity: string;
    hqState: string;
    hqZip: string;
    hqAddress: string;
}

// Represents a partner's detailed profile
export interface PartnerProfile {
    id?: string;
    companyId: string;
    partnerCompanyName: string;
    primaryVendorFocus: string[];
    geography: string[];
    currentCompetencies: string[];
    strategicGoals: string[];
    willingnessToInvest: string;
    opennessToServices: string;
    statedGrowthTargets: string;
}

export interface EntraAttributesSnapshot {
    country?: string;
    usageLocation?: string;
    state?: string;
    city?: string;
    officeLocation?: string;
    companyName?: string;
    department?: string;
    jobTitle?: string;
    employeeType?: string;
    employeeOrgData?: {
        division?: string;
        costCenter?: string;
    };
    extensionAttributes?: Record<string, string>;
    capturedAt?: string;
}

// Defines the structure for a user's profile
export interface UserProfile {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    companyId: string;
    companyName: string;
    companyType: CompanyType;
    jobTitle: string;
    roles: { [key: string]: any } | string[];
    abacTags?: string[];
    vendors?: string[];
    partners?: string[];
    navigationModules?: string[];
    navigationSubModules?: string[];
    country: string;
    countryCode?: string;
    region?: string;
    legalEntity?: string;
    entraAttributesSnapshot?: EntraAttributesSnapshot;
    city: string;
    state: string;
    zip: string;
    address: string;
    profileComplete: boolean;
    preferredLanguage: string;
    roleCategory: UserRoleCategory;
    completedKickstarts?: string[]; // Added for the Kickstart feature
    termsAccepted?: Date;
    termsAcceptedVersion?: string;
    /** When true, user receives latestTestVersionId for all versioned prompt configs */
    promptTestAccessEnabled?: boolean;
}

export enum UserRoleCategory {
    SALES = 'Sales',
    TECHNICAL = 'Technical',
    EXECUTIVE = 'Executive',
    MARKETING = 'Marketing',
    OPERATIONS = 'Operations'
}

// Structure for defining a role and its permissions
export interface Role {
    id?: string;
    name: string;
    permissions: string[];
}

// Simple type for the user's role in a simulation
export interface UserRole {
    name: string;
}

// --- SOLUTION NAVIGATOR TYPES ---
export interface Approach {
  title: string;
  strategicAlignment: string;
  description: string;
  painPointResolution: {
      painPoint: string;
      resolution: string;
  }[];
  keyDrivers: string[];
  pros: { business: string; technical: string }[];
  cons: { business: string; technical: string }[];
  components: {
    name: string;
    description: string;
    vendors: {
      vendorName: string;
      products: {
        productName: string;
        rationale: { business: string; technical: string };
      }[];
    }[];
  }[];
}

// Structure for the entire output of Stage 1
export interface SolutionNavigatorOutput {
    opportunitySummary: string;
    coreStrategy: string;
    keyPersonas: {
        persona: string;
        motivations: string;
    }[];
    approaches: Approach[];
}

// Defines the structure for the source metadata with distinct source types
export interface SourceSummary {
    freshnessStatement: string;
    internalSources: {
        title: string;
    }[];
    externalSources: {
        title: string;
        uri: string;
    }[];
}

// This interface now correctly represents the flat structure of curation scores
// by allowing any string key to have a value of string, number, or undefined.
export interface CurationScores {
    [key: string]: string | number | undefined;
}

export type SharedResourceType = 'growthPlan' | 'solution';
export type ResourceAccessRole = 'owner' | 'viewer';

export interface ResourceAccessMember {
    uid: string;
    role: ResourceAccessRole;
    displayName?: string;
    email?: string;
    grantedBy?: string;
    grantedAt?: string;
}

export interface ResourceAccessRecord {
    resourceType: SharedResourceType;
    resourceId: string;
    principalType: 'user';
    principalId: string;
    role: ResourceAccessRole;
    grantedBy: string;
    grantedAt: string;
}

// Structure for the new, detailed Sales Play generated by the Solution Navigator
export interface SalesPlay {
    id?: string;
    title: string;
    summary?: string;
    executiveSummary?: string;
    htmlContent?: string;
    /** Global Voice language code the sales play was generated in (e.g. 'en', 'de', 'pt-BR'). */
    language?: string;
    strategicAlignment: string;
    keyPersonas: {
        persona: string;
        motivations: string;
    }[];
    sellingStrategies: {
        title: string;
        description: string;
        keyDiscoveryQuestions: string[];
    }[];
    messagingGuide: {
        overallPitch: string;
        personaSpecific: {
            persona: string;
            painPoints: string[];
            valueProposition: string;
            talkingPoints: string[];
        }[];
    };
    betterTogether: {
        productName: string;
        description: string;
        hook: string;
        rationale: string;
    }[];
    competitivePositioning?: {
        competitor: string;
        ourAdvantage: string;
        talkingPoint: string;
    }[];
    objectionHandling?: {
        objection: string;
        response: string;
    }[];
    userInput?: any;
    solutionApproach?: Approach;
    valueProposition?: any;
    talkingPoints?: any;
    competitiveAngle?: any;
    suggestionsForImprovement?: string;
    createdAt?: any;
    createdBy?: string;
    status?: 'pending_review' | 'approved' | 'rejected' | 'archived';
    source?: string;
    tags?: { [key: string]: any };
    sourceSummary?: SourceSummary;
    /** External MCP / REST / Agent RAG sources consulted via the Trusted Context Gateway */
    consultedSources?: TrustedContextSourceSummary[];
    curationScores?: CurationScores;
    version?: number;
    sessionId?: string;
    members?: Record<string, ResourceAccessMember>;
    accessUserIds?: string[];
    sharingMode?: 'restricted';
}


// Structure for Prompt Configurations used in the Admin Console
export interface PromptConfiguration {
    id: string;
    ragEnabled?: boolean;
    [key: string]: any;
}

export interface GrowthPlan {
    id?: string;
    userId?: string;
    partnerId?: string;
    createdAt?: any;
    title?: string;
    planTitle?: string;
    htmlContent?: string;
    /** Global Voice language code the plan was generated in (e.g. 'en', 'de', 'pt-BR'). */
    language?: string;
    tags?: {
        partners: string[];
        vendors: string[];
    };
    userInput?: any;
    status?: 'pending_review' | 'approved' | 'rejected' | 'archived';
    source?: 'partnerGrowthPlanner' | 'manual';
    partnerSummary?: string;
    executiveSummary?: string; 
    currentAssessment?: string[];
    swotAnalysis: {
        strengths: string[];
        weaknesses: string[];
        opportunities: string[];
        threats: string[];
    };
    strategicRoadmap: {
        quarter: string;
        focusArea: string;
        keyActivities: string[];
    }[];
    roleBasedActionPlan?: {
        stakeholderName: string;
        role: string;
        responsibilities: string[];
    }[];
    enablementPlan: {
        tactic: string;
        description: string;
        timeline: string;
    }[];
    marketingPlan: {
        campaign: string;
        targetAudience: string;
        keyMessage: string;
        timeline: string;
    }[];
    demandGenPlan: {
        effort: string;
        channels: string;
        goal: string;
        timeline: string;
    }[];
    metricsForSuccess: {
        metric: string;
        target: string;
        type: string;
    }[];
    curationScores?: CurationScores;
    /** External MCP / REST / Agent RAG sources consulted via the Trusted Context Gateway */
    consultedSources?: TrustedContextSourceSummary[];
    generatedPlan?: {
        executiveSummary: any;
        strategicAnalysis: any;
        roleBasedActionPlan: any;
    };
    members?: Record<string, ResourceAccessMember>;
    accessUserIds?: string[];
    sharingMode?: 'restricted';
}

export interface PlanStakeholder {
    name: string;
    email: string;
    role?: string;
    background?: string;
    probableInterests?: string;
    potentialConcerns?: string;
}

export interface StrategicObjective {
    accountGoals: string;
    customerChallenges: string;
    valueProposition: string;
}

export interface StrategicAccountPlan {
    id?: string;
    userId?: string;
    partnerId?: string;
    createdAt?: any;
    title: string;
    planTitle?: string;
    htmlContent?: string;
    status?: 'pending_review' | 'approved' | 'rejected' | 'archived';
    source?: 'strategicAccountPlanner';
    
    accountInfo: {
        partnerName: string;
        industry: string | string[]; // Updated to support multi-select and retrocompatibility
        accountOwner: string;
        accountTier: string;
        annualRevenuePotential: string;
        currentARR: string;
    };
    stakeholders: {
        primaryContact: PlanStakeholder;
        decisionMaker: PlanStakeholder;
        internalChampion: string;
        otherStakeholders: PlanStakeholder[];
    };
    strategicObjectives: StrategicObjective[] | StrategicObjective; // Updated to support array or single object
    engagementPlan: {
        nextMeetingDate: string | null;
        plannedTouchpoints: string; 
        engagementActivities: string[];
        keyMilestones: string;
    };
    riskAssessment: {
        competitorPresence: string;
        riskLevel: string;
        identifiedRisks: string;
        expansionOpportunities: string;
    };
    actionItems: {
        nextSteps: string;
        supportNeeded: string;
    };
    
    generatedPlan?: {
        executiveSummary: string;
        strategicAnalysis: any;
        actionPlan: any;
    };
    curationScores?: CurationScores;
    tags?: any;
}

export interface GrowthPlanUserInput {
    scope: any;
    stakeholders: any;
    strategicFocus: any;
    businessModel: any;
    swot: {
        strengths: string;
        weaknesses: string;
        opportunities: string;
        threats: string;
    };
    goals: {
        primaryGoal: string;
        timeframe: string;
    };
    useExtendedSources?: boolean;
}

// Structure for a message in the Sales Simulator chat
export interface ChatMessage {
    id: string;
    sender: 'user' | 'persona' | 'assistant';
    persona?: string;
    text: string;
    timestamp: any;
    consultedSources?: TrustedContextSourceSummary[];
}

// Structure for a chat session in the Assistant
export interface ChatSession {
    id: string;
    title: string;
    lastMessage: string;
    lastUsedPersona?: string;
    isApiSession?: boolean;
}

// Structure for a stakeholder/persona in a scenario
export interface Stakeholder {
    id: string;
    name: string;
    title: string;
    motivation: string;
    rolePrompts: {
        [key: string]: any;
    };
    avatar: string;
    type: "Business" | "Technical" | "Advisor";
    roleType: "customer" | "tdSynnexVSR" | "googleFSR";
    simulatedResponses: Record<string, string>;
  }

// --- ACADEMY & SIMULATOR TYPES ---

export enum Difficulty {
    Beginner = 'Beginner',
    Intermediate = 'Intermediate',
    Expert = 'Expert',
}

export interface ScenarioDetails {
    businessChallenge: string;
    customerProfile: string;
    currentState: string;
    tdSynnexOpportunity: string;
    solution?: {
        primaryProduct: string;
        supportingProducts: string[];
    };
    quantifiedPainPoints?: any[];
}
// Structure for a Sales Simulator Scenario
export interface Scenario {
    id?: string;
    scenarioKey: string; 
    scenarioTitle: string;
    vertical: string;
    customerSize: string;
    geography: string;
    technologySegment: string;
    personas: Stakeholder[];
    creator: string;
    status: 'Draft' | 'Published' | 'Archived' | 'approved' | 'rejected' | 'pending';

    // The core scenario details are now nested under difficulty levels.
    scenarioDetails: {
        [key in Difficulty]: ScenarioDetails;
    };
}

// --- NEW SIMULATOR ARCHITECTURE ---

export type SimulatorType = 'SOLUTION_SELLING' | 'GROWTH_PLANNING' | 'PARTNER_RECRUITING';

export interface SalesScenarioData {
  opportunityBriefing: { [key: string]: any };
  stakeholders: Stakeholder[];
  salesMethodology: string;
  potentialObjections: string[];
  valueMappingActivity: MappingData;
}

export interface GrowthPlanScenarioData {
  partnerProfile: { [key: string]: any };
  swotAnalysis: { [key: string]: any };
  strategicGoals: { [key: string]: any };
}

export interface RecruitmentScenarioData {
  recruitmentTargetProfile: { [key: string]: any };
  strategicFitAnalysis: { [key: string]: any };
  outreachPlan: { [key: string]: any };
}

export interface AcademyScenarioOrigin {
  type: 'solutionNavigator';
  solutionId: string;
  solutionTitle?: string;
  requestedBy: string;
}

export interface AcademyScenario {
  id: string;
  title: string;
  description: string;
  simulatorType: SimulatorType;
  scenarioData: SalesScenarioData | GrowthPlanScenarioData | RecruitmentScenarioData;
  origin?: AcademyScenarioOrigin;
  difficulty?: ScenarioDifficulty; // Added: Optional difficulty level
  status?: string; // Added: Status field
  tags?: {
      vendors?: string[]; // Changed from vendor: string to vendors: string[]
      products?: string[];
      vertical?: string;
      technologySegment?: string; // Changed/Added: technologySegment
      geography?: string;
      // Legacy support during migration (optional)
      vendor?: string; 
      technologies?: string[];
  };
  createdAt: any;
  updatedAt?: any; // Added: updatedAt
  tagsUpdatedAt?: any; // Added
  contentUpdatedAt?: any; // Added
  version: number;
  governance?: {
    lastReviewAt: any;
    nextReviewAt: any;
    relevanceScore: number;
    accuracyScore: number;
    qualityScore: number;
    reviewSummary: string;
    requiresHumanReview: boolean;
  };
}

export enum ScenarioDifficulty {
    Beginner = 'Beginner',
    Intermediate = 'Intermediate',
    Advanced = 'Advanced'
}

// --- Interaction & Evaluation Types ---

export interface Evaluation {
    score: number;
    feedback: string;
}

export interface PinnedInsight {
    id: string;
    text: string;
    phase: string;
}

export interface GeneratedObjection {
    id: string;
    category: string;
    objection: string;
    response: {
        summary: string;
        acknowledge: string;
        address: string;
        pivot: string;
    };
}

export interface ObjectionEvaluation {
    score: number;
    feedback: string;
}

export interface FinalFeedback {
    overallScore: number;
    overallSummary: string;

    feedback: string;
    strengths: string[];
    areasForImprovement: string[];
    pillarScores?: {
        solutionSelling: number;
        vendorAcumen: number;
        businessValue: number;
        eq: number;
    };
    detailedAnalysis?: string;
    suggestedEnablement?: string[];
    continuousImprovementTips?: string[];
}

// This represents the structured data for the proposal itself, generated by the AI.
export interface ProposalData {
    introduction: string;
    proposedSolution: {
        title: string;
        description: string;
    };
    keyBenefits: Array<{
        benefit: string;
        explanation: string;
    }>;
    nextSteps: string[];
}

export interface MappingData {
    painPoints: string[];
    solutionValues: string[];
    businessOutcomes: string[];
}

export interface MappingEvaluation {
    score: number;
    feedback: string;
}

// This represents the final, self-contained evaluation object that is stored in the Zustand store.
// It includes the user's input (scores, comments), the AI's feedback on that input (feedback, score),
// and the original proposal that was evaluated.
export interface ProposalEvaluation {
    scores: Record<string, number>;
    comments: string;
    feedback?: string;
    score?: number; // The score for the quality of the user's critique
    originalProposal: ProposalData;
    userCritique?: {
        rating: number;
        comments: string;
    }
}

// This represents the direct output from the AI cloud function that evaluates the user's critique.
export interface ProposalCritique {
    feedback: string;
    score: number; // The score for the quality of the user's critique
}


// --- Service & Request Types ---

export interface Request {
    [key: string]: any;
}

export interface SolutionRequest {
    [key:string]: any;
}

// --- Synapse Library & Content Management ---
export enum ContentType {
    BRANDING_ASSET = 'Branding Asset',
    SALES_PLAY = 'Sales Play',
    GROWTH_PLAN = 'Growth Plan',
    WHITE_PAPER = 'White Paper',
    MARKETING_ASSET = 'Marketing Asset',
    SALES_DECK = 'Sales Deck',
    VALUE_PROPOSITION = 'Value Proposition',
    LEARNING_ENABLEMENT = 'Learning & Enablement',
    PROGRAMS_POLICIES = 'Programs and Policies',
    LICENSING_DETAILS = 'Licensing Details',
    PROPRIETARY_CONFIDENTIAL = 'Proprietary & Confidential',
    RESEARCH_REPORT = 'Research Report'
}

export interface OnCallSaveContentToLibraryRequest {
    title: string;
    description: string;
    htmlContent: string;
    contentType: ContentType | 'sales_play' | 'knowledge_base' | 'unknown';
    tags: {
        companies?: string[];
        vendors?: string[];
        partners?: string[];
        technologies?: string[];
        verticals?: string[];
    };
}

export interface ContentItem {
    id: string;
    title: string;
    description?: string;
    source: string;
    status: string;
    isPublic?: boolean;
    createdAt?: any;
    updatedAt?: any;
    createdBy?: string;
    createdByName?: string;
    createdByEmail?: string;
    contentType: ContentType | 'sales_play' | 'knowledge_base' | 'unknown';
    tags?: {
      companies?: string[];
      vendors?: string[];
      partners?: string[];
      technologies?: string[];
      verticals?: string[];
      roles?: string[];
      abacTags?: string[];
      isPublic?: boolean;
      simulation?: boolean;
      review?: boolean;
      scenarioId?: string;
      userId?: string;
    };
    fileUrl?: string;
    fileName?: string;
    fileType?: string;
    storagePath?: string;
    gcsUri?: string;
    htmlContent?: string;
    curationScores?: CurationScores;
    sourceSummary?: SourceSummary;
    /** Global Voice language code the content was generated in. Absent means English. */
    language?: string;

    // New fields for the Three-Layer Architecture
    structuredContent: SalesPlay | null;
    lastRescoredAt: any | null;
    obsolescenceSummary: string | null;
    members?: Record<string, ResourceAccessMember>;
    accessUserIds?: string[];
    sharingMode?: 'restricted';
}

// Represents a single document in the 'taxonomies' collection.
export interface TaxonomyItem {
    id: string;   // The Firestore document ID
    type: string; // The category of the item, e.g., 'verticals', 'regions'
    name: string; // The display name of the item, e.g., 'Healthcare', 'North America'
    parent?: string; // Optional field to link to a parent taxonomy item (e.g., a country to a region)
}

export interface Taxonomy {
    id: string;
    items: TaxonomyItem[];
}


// --- Prompt Management Types ---
export interface PersonaDetail {
    name: string;
    avatar: string;
    systemPrompt: string;
    systemInstruction?: string;
    apiOnly?: boolean;
}

export type ThinkingLevelOption = 'OFF' | 'MINIMAL' | 'LOW' | 'MEDIUM' | 'HIGH';

export interface ModelThinkingCapability {
    modelName: string;
    supportsThinking: boolean;
    thinkingType: 'level' | 'budget' | 'none';
    availableThinkingLevels: ThinkingLevelOption[];
    canDisableThinking: boolean;
    defaultThinkingLevel?: ThinkingLevelOption;
    description: string;
}

export interface PromptDetail {
    name: string;
    description: string;
    modelName: string;
    prompt: string;
    systemInstruction?: string;
    thinkingLevel?: ThinkingLevelOption;
    temperature?: number;
    jsonStructure?: any;
    enableRAG?: boolean;
    enableSearchGrounding?: boolean;
    enableContextGateway?: boolean;
}

export interface PromptConfig {
    id: string;
    displayName: string;
    modelName: string;
    enableRAG?: boolean;
    enableSearchGrounding?: boolean;
    enableContextGateway?: boolean;
    personas?: Record<string, PersonaDetail>;
    routerPrompt?: string;
    [promptKey: string]: PromptDetail | string | boolean | undefined | Record<string, PersonaDetail>;
}

/** Parent document in the versioned `prompt_configurations` collection */
export interface PromptVersionConfig {
    id: string;
    displayName: string;
    currentVersionId?: string;
    latestTestVersionId?: string;
    createdAt?: any;
    updatedAt?: any;
    isBaselineOnly?: boolean;
}

/** Content payload stored inside an immutable version document (synapseAssistant shape) */
export interface SynapseAssistantVersionContent {
    modelName: string;
    thinkingLevel?: ThinkingLevelOption;
    temperature?: number;
    enableRAG?: boolean;
    enableSearchGrounding?: boolean;
    enableContextGateway?: boolean;
    routerPrompt: string;
    prompt?: string;
    systemInstruction?: string;
    personas: Record<string, PersonaDetail>;
}

/** Immutable version document under prompt_configurations/{configId}/versions */
export interface PromptConfigVersion {
    id: string;
    content: SynapseAssistantVersionContent | Record<string, any>;
    versionNumber: number;
    createdAt?: any;
    createdBy?: string;
    createdByName?: string;
    createdByEmail?: string;
}

export interface SafetySettings {
    HARM_CATEGORY_HARASSMENT: string;
    HARM_CATEGORY_HATE_SPEECH: string;
    HARM_CATEGORY_SEXUALLY_EXPLICIT: string;
    HARM_CATEGORY_DANGEROUS_CONTENT: string;

    HARM_CATEGORY_CIVIC_INTEGRITY: string;
}

// --- Researcher & Scenario Generator Types ---

export enum ProcessState {
    IDLE,
    LOADING_ANALYSIS,
    ANALYSIS_COMPLETE,
    LOADING_SCENARIOS,
    GENERATION_COMPLETE,
    ERROR,
  }
  
  // Legacy input, retained for compatibility with scenario generator
  export interface ScenarioInput {
    vendor: string;
    horizon: string;
    region: string;
    country: string;
    totalSolutions: number;
    regionRank: number;
    countryRank: number;
  }
  
  export enum ResearchType {
    TOP_WORKLOADS = 'Top End Customer Workloads/Use Case Research',
    PARTNER_RECRUITMENT = 'Partner Recruitment Targeting and Qualification',
    PARTNER_PROFILE_REPORT = 'Partner Profile Report', // New research type
  }

  export interface BaseResearchInput {
    researchType: ResearchType;
    /** Optional Global Voice language code for the generated report. Defaults to English. */
    language?: string;
    /** When true, consults external vendor & partner sources via the Trusted Context Gateway. */
    useExtendedSources?: boolean;
  }

  export interface TimeHorizon {
      startDate?: Date;
      endDate?: Date;
  }

  export interface TopWorkloadsResearchInput extends BaseResearchInput {
    researchType: ResearchType.TOP_WORKLOADS;
    vendors: string[];
    timeHorizon: TimeHorizon;
    verticalMarkets?: string[];
    regions: string[];
    countries?: string[];
    numberOfWorkloadsPerRegion: number;
    numberOfWorkloadsPerCountry?: number;
    numberOfWorkloadsPerVertical?: number;
  }

  export interface PartnerRecruitmentResearchInput extends BaseResearchInput {
    researchType: ResearchType.PARTNER_RECRUITMENT;
    vendors: string[];
    timeHorizon: TimeHorizon;
    verticalMarketFocus?: string[];
    regions: string[];
    countries?: string[];
    numberOfPartnersPerRegion: number;
    numberOfPartnersPerCountry?: number;
    numberOfPartnersPerVertical?: number;
  }
  
  // New input type for the Partner Profile Report
  export interface PartnerProfileResearchInput extends BaseResearchInput {
    researchType: ResearchType.PARTNER_PROFILE_REPORT;
    partnerName: string; 
    partnerWebsiteUrl: string;
    vendors: string[];
  }

  export type ResearcherInput = TopWorkloadsResearchInput | PartnerRecruitmentResearchInput | PartnerProfileResearchInput;

  export type ScenarioGenerationTarget = 'Overall' | 'Region' | 'Country';
  
  export interface GeneratedScenario {
    scenarioKey: string;
    scenarioTitle: string;
    scenarioDetails: GeneratedScenarioDetails;
    personas: GeneratedPersona[];
    objectionHandler: Record<string, GeneratedObjection>;
    guidanceEvents: GuidanceEvent[];
    dealInjects: DealInject[];
  }
  
  export interface GeneratedScenarioDetails {
    customerProfile: string;
    currentState: string;
    quantifiedPainPoints: string[];
    businessChallenge: string;
    googleSolution: { 
      primaryProduct: string;
      supportingProducts: string[];
    };
    tdSynnexOpportunity: string;
    vertical: string;
    customerSize: string;
  }
  
  export interface GeneratedPersona {
    id: string;
    name: string;
    type: "Business" | "Technical" | "Advisor";
    roleType: "customer" | "tdSynnexVSR" | "googleFSR"; 
    avatar: string;
    motivation: string;
    rolePrompts: {
        [key: string]: string;
    };
    simulatedResponses: Record<string, string>;
  }
  
  export interface GeneratedObjection {
    objection: string;
    acknowledge: string;
  }
  
  export interface GuidanceEvent {
    eventId: string;
    trigger: string;
    presenterPersonaId: string;
    question: string;
    choices: Choice[];
  }
  
  export interface Choice {
    text: string;
    points: number;
    feedback: string;
    isCorrect: boolean;
  }
  
  export interface DealInject {
    injectId: string;
    trigger: {
      type: string;
      value: number;
    };
    alertTitle: string;
    alertDescription: string;
    effects: Effect[];
  }
  
  export interface Effect {
    type: string;
    payload: string;
  }

  export interface Component {
    name: string;
    description: string;
    vendors: Vendor[];
  }
  export interface KeyPersona {
      persona: string;
      motivations: string;
  }
  export interface PainPointResolution {
      painPoint: string;
      resolution: string;
  }
  export interface Vendor {
      vendorName: string;
      products: {
          productName: string;
          rationale: {
              business: string;
              technical: string;
          };
      }[];
  }

export interface SalesMethodology {
    id: string;
    name: string;
    description: string;
    items: {
        key: string;
        label: string;
        description: string;
    }[];
}

export interface ScoreState {
    totalScore: number;
    actionsTaken: Set<string>;
}

export interface SalesSimulatorState {
    scenario: AcademyScenario | null;
    scenarioData: SalesScenarioData | null;
    role: UserRole | null;
    methodology: SalesMethodology | null;
    currentPhase: number;
    highestCompletedPhaseIndex: number;
    scores: ScoreState;
    chatHistory: ChatMessage[];
    notepadEntries: string[];
    completedMethodologyTasks: string[];
    valueMappingEvaluation: MappingEvaluation | null;
    proposalEvaluation: ProposalEvaluation | null;
    finalFeedback: FinalFeedback | null;
    isLoading: boolean;
    error: string | null;
    reviewContent: ContentItem | null;
    isCompleted: boolean;

    startSession: (scenario: AcademyScenario) => void;
    setRole: (role: UserRole | null) => void;
    setMethodology: (methodology: SalesMethodology | null) => void;
    setPhase: (phase: number) => void;
    addScore: (score: number, phase: string, activity: string) => void;
    addChatMessage: (message: ChatMessage) => void;
    addNotepadEntry: (entry: string) => void;
    setNotepadEntries: (entries: string[]) => void;
    updateMethodologyAdherence: () => void;
    completeValueMapping: (evaluation: MappingEvaluation) => void;
    completeProposalEvaluation: (evaluation: Partial<ProposalEvaluation>) => void;
    generateFinalFeedback: () => void;
    endSession: () => void;
    generateAndCompleteFinalReview: () => void;
}

// --- BATCH GENERATION TYPES ---

export interface WorkloadDetail {
    title: string;
    description: string;
    vendor: string;
    geography?: string;
    vertical?: string;
    customerSize?: string;
}

// --- ACADEMY COURSE DYNAMIC RENDERING ---

export type CourseBlockType = 'text' | 'hero' | 'video' | 'flashcards' | 'stepper' | 'quiz' | 'image' | 'pdf' | 'download' | 'html' | 'exam' | 'course_completion';

export type VideoSourceType = 'youtube' | 'upload' | 'embed';
export type VideoProvider = 'youtube' | 'synthesia' | 'vimeo' | 'loom' | 'wistia' | 'stream' | 'dailymotion' | 'other' | 'upload';

export interface CourseVideoContent {
  url: string;
  sourceType?: VideoSourceType;
  provider?: VideoProvider;
  embedUrl?: string;
  rawInput?: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  prompt?: string;
  originalPrompt?: string;
}

export interface CourseBlock {
  id: string;
  type: CourseBlockType;
  layout: '1-col' | '2-col';
  title?: string;
  description?: string;
  content: any; // Dynamic based on type (e.g., flashcard array, quiz question array, CourseVideoContent, etc.)
  secondaryType?: CourseBlockType; // Type for the second column
  secondaryContent?: any; // Content for the second column
  backgroundImage?: string; // Background image for the block
  backgroundGradient?: string; // CSS background color or gradient for the block
}

export interface CourseSection {
  id: string;
  title: string;
  description?: string;
  backgroundGradient?: string;
  textColor?: string;
  blocks: CourseBlock[];
}

export interface CourseEditRecord {
  userId: string;
  userName?: string;
  userEmail?: string;
  timestamp: string; // ISO String
  action?: string;   // e.g., 'Created course', 'Updated course content', 'Published course', 'Duplicated course'
}

/** Per-course sharing profile. */
export type CourseAccessRole = 'manager' | 'collaborator' | 'reviewer';

export interface CourseMember {
  uid: string;
  role: CourseAccessRole;
  displayName?: string;
  email?: string;
  grantedBy?: string;
  grantedAt?: string;
}

export interface CourseDimensionEvaluation {
  score: number;      // 0-100
  comment: string;    // General comment on this dimension
}

export type CourseFreshnessStatus = 'up_to_date' | 'minor_updates_needed' | 'outdated_or_incorrect';

export interface CourseQualityRating {
  overallScore: number;               // 0-100 (plunged if outdated/false info is detected)
  evaluatedAt: string;                // ISO timestamp
  contentHash: string;                // Deterministic hash of course content when rated
  summary: string;                    // Executive summary of course quality
  hasCriticalAccuracyIssue: boolean;  // True when outdated or false information plunged the score
  freshnessStatus: CourseFreshnessStatus;
  outdatedOrFalseClaims: string[];    // Specific outdated or inaccurate statements detected
  dimensions: {
    accuracyAndFreshness: CourseDimensionEvaluation;
    contentDepth: CourseDimensionEvaluation;
    structure: CourseDimensionEvaluation;
    interactivity: CourseDimensionEvaluation;
    assessment: CourseDimensionEvaluation;
  };
}

export interface AcademyCourse {
  id?: string;
  slug: string;
  title: string;
  description: string;
  heroGradient?: string;
  heroImage?: string; // New: Hero background image
  introText?: string;
  blocks?: CourseBlock[]; // Deprecated: Use sections instead
  sections?: CourseSection[];
  sourceDocuments?: { path: string; name: string; url: string; mimeType: string }[];
  audience?: string;
  intent?: string;
  depth?: string;
  language?: string;
  createdAt?: any;
  updatedAt?: any;
  createdBy?: string;
  createdByName?: string;
  createdByEmail?: string;
  updatedBy?: string;
  updatedByName?: string;
  updatedByEmail?: string;
  status: 'Draft' | 'Published';
  isFunded?: boolean;
  fundingAmountUsd?: number;
  editHistory?: CourseEditRecord[];
  /** Per-course ACL keyed by uid. */
  members?: Record<string, CourseMember>;
  /** Query index: users with any course role (manager, collaborator, reviewer). */
  accessUserIds?: string[];
  /** Automated AI quality and freshness rating across 5 dimensions. */
  qualityRating?: CourseQualityRating;
}

export interface CourseBlockComment {
  id?: string;
  courseId: string;
  sectionId?: string;
  blockId?: string;       // Specific block ID or 'general'
  blockTitle?: string;
  userId: string;
  userName: string;
  userEmail?: string;
  comment: string;
  status: 'open' | 'resolved';
  createdAt: string;       // ISO string
  resolvedAt?: string;
  resolvedBy?: string;
  resolvedByName?: string;
}

export interface UserCourseProgress {
    id?: string;
    userId: string;
    courseId: string;
    courseSlug: string;
    courseTitle: string;
    lastAccessedSectionIndex: number;
    completed: boolean;
    bestScore?: number;
    completedAt: Date | null;
    lastVisitedAt: Date;
}

// ---------------------------------------------------------------------------
// Partner Intelligence
// ---------------------------------------------------------------------------

export type PartnerIntelligenceAction = 'contact_check' | 'latest_news' | 'partner_insights';

export interface ContactCheckOutput {
  status: 'YES' | 'NO' | 'UNKNOWN';
  reliabilityPercentage: number;
  explanation: string;
  title: string | null;
  position: string | null;
  professionalEmail: string | null;
  department: string | null;
  sourcesChecked: string[];
}

export interface PartnerNewsItem {
  title: string;
  date: string;
  source: string;
  content: string;
  url: string | null;
  category: string;
  sentiment: 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE';
  relevanceScore: number;
  keyTakeaway: string;
}

export interface LatestNewsOutput {
  companyName: string;
  totalFound: number;
  news: PartnerNewsItem[];
}

export interface InsightSection {
  areaId: string;
  areaTitle: string;
  content: string;
  keyTakeaways: string[];
  confidenceLevel: 'HIGH' | 'MEDIUM' | 'LOW';
}

export interface PartnerInsightsOutput {
  companyName: string;
  sourceReportId: string | null;
  isCached: boolean;
  sections: InsightSection[];
}

export interface PartnerIntelligenceRequestData {
  action: PartnerIntelligenceAction;
  contactCheck?: {
    contactName?: string;
    contactEmail?: string;
    companyName: string;
    vatNumber?: string;
    domain?: string;
  };
  latestNews?: {
    companyName: string;
    limit?: number;
    timeframe?: string;
    categories?: string[];
  };
  partnerInsights?: {
    companyName: string;
    companyId?: string;
    areasOfInterest: string[];
    forceFresh?: boolean;
  };
}

export interface PartnerIntelligenceResponse<T = any> {
  success: boolean;
  action: PartnerIntelligenceAction;
  timestamp: string;
  data: T;
}

// ---------------------------------------------------------------------------
// Synapse News & Platform Announcements
// ---------------------------------------------------------------------------

export type NewsArticleStatus = 'draft' | 'published' | 'archived';

export type NewsArticleCategory =
  | 'new_feature'
  | 'enhancement'
  | 'announcement'
  | 'enablement';

export interface LocalizedNewsContent {
  title: string;
  summary: string;
  bodyHtml: string;
  ctaLabel?: string;
}

export interface NewsArticle {
  id: string;
  slug: string;
  title: string;
  summary: string;
  bodyHtml: string;
  coverImageUrl?: string;
  category: NewsArticleCategory;
  targetModules: string[];
  ctaLabel?: string;
  ctaPath?: string;
  isPinned: boolean;
  status: NewsArticleStatus;
  publishedAt?: any;
  createdAt?: any;
  updatedAt?: any;
  authorId: string;
  authorName: string;
  authorEmail?: string;
  translations?: Record<string, LocalizedNewsContent>;
}

// ---------------------------------------------------------------------------
// Trusted Context Gateway (TCG)
// ---------------------------------------------------------------------------

/**
 * Pluggable connector protocol identifier.
 * Additional string keys can be registered dynamically in ConnectorRegistry.
 */
export type GatewayProtocol = 'mcp' | 'rest_api' | 'vertex_rag_app' | (string & {});

/**
 * Credential resolution strategy. Raw secrets are NEVER stored in Firestore or frontend state.
 */
export type GatewayAuthType =
  | 'none'
  | 'gcp_adc'
  | 'secret_manager_api_key'
  | 'secret_manager_bearer'
  | 'oauth2_client_credentials';

export interface GatewayAuthConfig {
  type: GatewayAuthType;
  /** Secret name in Google Cloud Secret Manager (or process.env fallback), NEVER the raw secret value. */
  secretNameRef?: string;
  /** HTTP header name for secret_manager_api_key (defaults to 'x-api-key'). */
  headerName?: string;
  /** OAuth 2.0 token endpoint URL for oauth2_client_credentials. */
  tokenEndpoint?: string;
  /** Secret Manager reference for OAuth 2.0 client_id. */
  clientIdSecretRef?: string;
  /** Secret Manager reference for OAuth 2.0 client_secret. */
  clientSecretSecretRef?: string;
  /** Optional OAuth 2.0 scopes. */
  scopes?: string[];
}

/**
 * Two-level ABAC & Tag permission policy for a knowledge source.
 * Uses the unified TD-SYNapse tag vocabulary ('public', 'vendor:...', 'partner:...', 'company:...', 'region:...', 'country:...', 'role:...').
 */
export interface GatewayAccessPolicy {
  /** If true, any authenticated user can request and use this knowledge source. */
  isPublic: boolean;
  /**
   * OR logic: When isPublic is false, the user must possess AT LEAST ONE of these tags
   * in their effective ABAC tag set (e.g. ['vendor:Microsoft', 'partner:contoso']).
   */
  requiredAnyTags?: string[];
  /**
   * AND logic: When isPublic is false, the user must possess EVERY tag in this list
   * (e.g. ['vendor:Cisco', 'region:EMEA']).
   */
  requiredAllTags?: string[];
  /**
   * Explicit deny tags: If the user possesses any of these tags (unless unrestricted admin), access is denied.
   */
  excludedTags?: string[];
  /**
   * For multi-tenant RAG sources (e.g. vertex_rag_app ingesting SharePoint/Drive),
   * whether to also inject buildAbacRagFilter() into the document search request.
   */
  enforceDocumentLevelAbac?: boolean;
}

export interface GatewaySourceConfig {
  id: string;
  displayName: string;
  vendor: string;
  protocol: GatewayProtocol;
  enabled: boolean;
  /** HTTPS endpoint URL for MCP or REST API sources. */
  endpointUrl?: string;
  /** HTTP method for REST API sources (defaults to 'GET'). */
  httpMethod?: 'GET' | 'POST';
  /** For vertex_rag_app sources: Vertex AI Search Engine/App ID or Data Store ID (or env var reference). */
  vertexDataStoreOrEngineId?: string;
  /** Optional GCP location for vertex_rag_app (defaults to 'global'). */
  vertexLocation?: string;
  /** Rich semantic description of what domain concepts, products, and questions this source answers. */
  semanticCapabilities: string;
  /** Available MCP tool names or REST parameter hints to guide the Semantic Context Planner. */
  parameterHints?: string;
  /** Default MCP tool name when protocol === 'mcp'. */
  defaultMcpToolName?: string;
  /** Default query params merged into REST API requests. */
  defaultQueryParams?: Record<string, string>;
  accessPolicy: GatewayAccessPolicy;
  authConfig: GatewayAuthConfig;
  timeoutMs?: number;
}

export interface TrustedContextSourceSummary {
  sourceId: string;
  displayName: string;
  vendor: string;
  protocol: GatewayProtocol;
  toolOrEndpoint: string;
  latencyMs: number;
  status: 'success' | 'skipped' | 'error';
  citationUrl?: string;
}

